import { research, token, report, calendar, chart, buildings, people } from "../assets/icons"
import { isFrenchPath } from "../internationalize/i18n.service"

/** Content types for search (language-agnostic) */
export enum ContentEnum {
  ARTICLE = 'article',
  REPORTS = 'reports',
  EVENT = 'event',
  CHARTS = 'charts',
  COMPANIES = 'companies',
  PEOPLE = 'people',
  ASSETS = 'assets'
}

/** EN index name → FR index name */
export const CONTENT_INDEX_MAP: Record<ContentEnum, { en: string; fr: string }> = {
  [ContentEnum.ARTICLE]: { en: 'article-en', fr: 'article' },
  [ContentEnum.REPORTS]: { en: 'reports-en', fr: 'reports-fr' },
  [ContentEnum.EVENT]: { en: 'event-en', fr: 'event-fr' },
  [ContentEnum.CHARTS]: { en: 'charts-en', fr: 'charts-fr' },
  [ContentEnum.COMPANIES]: { en: 'companies-en', fr: 'companies-fr' },
  [ContentEnum.PEOPLE]: { en: 'people-en', fr: 'people-fr' },
  [ContentEnum.ASSETS]: { en: 'assets-en', fr: 'assets-fr' }
}

/** Index name → content type (for reverse lookup) */
const INDEX_TO_CONTENT: Record<string, ContentEnum> = Object.values(ContentEnum).reduce(
  (acc, contentType) => {
    const { en, fr } = CONTENT_INDEX_MAP[contentType]
    acc[en] = contentType
    acc[fr] = contentType
    return acc
  },
  {} as Record<string, ContentEnum>
)

export interface ContentOptions {
  name: string
  icon: string
  limit: number
  sort: string
  hasDate: boolean
  isIcon: boolean
}

export const defaultOptions: Partial<ContentOptions> = {
  hasDate: true,
  isIcon: false
}

const contentOptions = new Map<ContentEnum, Partial<ContentOptions>>([
  [ContentEnum.ARTICLE, { icon: research, sort: 'createdOn:desc', limit: 1 }],
  [ContentEnum.REPORTS, { icon: report, sort: 'createdOn:desc', limit: 1 }],
  [ContentEnum.EVENT, { icon: calendar, hasDate: false, limit: 1 }],
  [ContentEnum.CHARTS, { icon: chart, hasDate: false, limit: 1, isIcon: true }],
  [ContentEnum.COMPANIES, { icon: buildings, hasDate: false, limit: 5, isIcon: true }],
  [ContentEnum.PEOPLE, { icon: people, hasDate: false, limit: 5, isIcon: true }],
  [ContentEnum.ASSETS, { icon: token, hasDate: false, limit: 5, isIcon: true }],
])

/** Returns the index names to search based on current pathname language */
export const getIndexes = (): string[] => {
  const useFrench = isFrenchPath()
  return Object.values(ContentEnum).map(
    contentType => CONTENT_INDEX_MAP[contentType][useFrench ? 'fr' : 'en']
  )
}

/** Resolves index UID to content type for options lookup */
const getContentTypeFromIndex = (indexUid: string): ContentEnum | undefined =>
  INDEX_TO_CONTENT[indexUid]

export const getOptions = (contentTypeOrIndexUid: ContentEnum | string) => {
  const contentType = getContentTypeFromIndex(contentTypeOrIndexUid as string) ?? contentTypeOrIndexUid as ContentEnum
  return Object({ ...defaultOptions, ...contentOptions.get(contentType) }) as ContentOptions
}

export const getIcon = (contentType: ContentEnum | string) => getOptions(contentType).name

/** Returns content type for i18n key (e.g. 'content.article', 'content.reports') */
export const getContentTypeForI18n = (indexUid: string): string =>
  getContentTypeFromIndex(indexUid) ?? indexUid
